import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isBefore, startOfDay } from 'date-fns';
import { Building2, Loader2, RefreshCw, Settings, Ticket } from 'lucide-react';
import moment from 'moment';

import { Button, EmptyState, FilterTabs, PageHeader, Surface, Text } from '@/components/flup';
import ContextPicker, { PickerOption } from '@/components/slotManager/ContextPicker';
import DayBar from '@/components/slotManager/DayBar';
import DaySummary from '@/components/slotManager/DaySummary';
import SlotBoard from '@/components/slotManager/SlotBoard';
import SelectionBar from '@/components/slotManager/SelectionBar';
import VenuePlanPill from '@/components/slotManager/VenuePlanPill';
import {
  Slot,
  SlotFilter,
  apiDate,
  filterCounts,
  getSlotView,
  summarise,
} from '@/components/slotManager/slotUtils';
import CreateBookingModal from '@/components/CreateBookingModal';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';

import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import {
  getCompletedVenueDetails,
  getSlotsByDate,
  getVendorVenueDetailsById,
  toggleBookingByVenueId,
  updateVendorVenueSlots,
} from '@/store/thunkMidddleware/turfMiddleware';
import { createDirectBooking, createManualBooking } from '@/store/thunkMidddleware/paymentMiddleware';
import { setCurrentOnboardVenueDetails } from '@/store/slices/turfSlice';
import { setSelectedCourtId, setSelectedSportId } from '@/store/slices/global.Slice';
import { Values } from '@/constants/Values';
import Strings from '@/constants/String';
import routes from '@/routes/routes.json';

/**
 * Slot Manager — the vendor's home.
 *
 * The screen this replaces asked a vendor to walk a list of venues, open one,
 * pick a sport from a dropdown, pick a court from a second dropdown, then pick
 * a date from a popover before a single slot appeared. Five decisions before
 * any work. Here every one of them defaults — first venue, first sport, first
 * court, today — so the board is on screen the moment the page loads, and the
 * pickers above it exist only for the vendor who wants a *different* answer.
 *
 * The venue a vendor last worked on is remembered, because "the one I was just
 * looking at" is a better default than "the first one" on a return visit.
 */

const LAST_VENUE_KEY = 'byz.slotManager.lastVenueId';

const SlotManager = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const globalstate = useAppSelector((state: RootState) => state.globalstate);
  const {
    completedVenueDetails,
    VendorVenueDetailsById,
    getSlotsByDateResponse,
    updateVendorVenueSlotsResponse,
    toggleBookingByVenueIdResponse,
  } = useAppSelector((state: RootState) => state.turf);

  const [venueId, setVenueId] = useState<string>(params?.id || '');
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [slotFilter, setSlotFilter] = useState<SlotFilter>('upcoming');

  const sportId = globalstate?.selectedSportId;
  const courtId = globalstate?.selectedCourtId;

  const venue = VendorVenueDetailsById?.payload;
  const slots: Slot[] = getSlotsByDateResponse?.payload?.slots || [];
  const slotDuration = getSlotsByDateResponse?.payload?.courtDetails?.slotDurationInMinutes;

  /* ---------------------------------------------------------------- venues */

  /** Only venues that are actually manageable — paid onboarding, verified. */
  const venues = useMemo(
    () =>
      (completedVenueDetails?.payload || []).filter(
        (v: any) => v?.oneTimeOnboardingFeeDetails?.isPaymentdone,
      ),
    [completedVenueDetails?.payload],
  );

  useEffect(() => {
    dispatch(getCompletedVenueDetails({ callback: () => undefined }));
  }, [dispatch]);

  // Land on a venue without being asked: the URL wins, then the one last
  // worked on, then the first live venue, then simply the first.
  useEffect(() => {
    if (venueId || !venues.length) return;

    const remembered = sessionStorage.getItem(LAST_VENUE_KEY);
    const preferred =
      venues.find((v: any) => v._id === params?.id) ||
      venues.find((v: any) => v._id === remembered) ||
      venues.find((v: any) => v?.isVerified) ||
      venues[0];

    if (preferred?._id) setVenueId(preferred._id);
  }, [venues, venueId, params?.id]);

  // Fetch the chosen venue and keep the URL in step, so a deep link and a
  // browser refresh both come back to the same board.
  useEffect(() => {
    if (!venueId) return;

    sessionStorage.setItem(LAST_VENUE_KEY, venueId);
    if (params?.id !== venueId) {
      navigate(`${routes.venueManagement}/${venueId}`, { replace: true });
    }

    setSelectedIds([]);
    dispatch(getVendorVenueDetailsById({ pathparams: venueId, callback: () => undefined }));
    // `params.id` is an output of this effect, not an input to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, dispatch]);

  /* ------------------------------------------------------- sports & courts */

  const sports = venue?.sports || [];
  const activeSport = useMemo(
    () => sports.find((s: any) => s?._id === sportId),
    [sports, sportId],
  );
  const courts = activeSport?.courts || [];

  // Default the sport to the venue's first whenever the current one doesn't
  // belong to this venue — which is exactly what happens on a venue switch.
  useEffect(() => {
    if (!sports.length) return;
    if (!sports.some((s: any) => s?._id === sportId)) {
      dispatch(setSelectedSportId(sports[0]?._id));
    }
  }, [sports, sportId, dispatch]);

  useEffect(() => {
    if (!courts.length) {
      if (courtId) dispatch(setSelectedCourtId(''));
      return;
    }
    if (!courts.some((c: any) => c?._id === courtId)) {
      dispatch(setSelectedCourtId(courts[0]?._id));
    }
  }, [courts, courtId, dispatch]);

  /* ----------------------------------------------------------------- slots */

  /**
   * The sport and court live in global state, so they survive a venue switch
   * and keep pointing at the previous venue until the new venue's details land
   * and the reconcile effects above move them. Fetching in that window asks for
   * a court this venue does not own, and is then immediately followed by a
   * second, correct request — so wait until the selection actually belongs to
   * the venue that is loaded.
   */
  const selectionMatchesVenue =
    venue?._id === venueId && courts.some((c: any) => c?._id === courtId);

  const fetchSlots = useCallback(() => {
    if (!venueId || !sportId || !courtId || !selectionMatchesVenue) return;
    dispatch(
      getSlotsByDate({
        payload: { venueId, sportId, courtId, date: apiDate(selectedDate) },
        callback: () => undefined,
      }),
    );
  }, [dispatch, venueId, sportId, courtId, selectedDate, selectionMatchesVenue]);

  useEffect(() => {
    setSelectedIds([]);
    fetchSlots();
  }, [fetchSlots]);

  // A past date has nothing upcoming by definition, so the default filter
  // would render an empty board. Reviewing history is the only thing that
  // makes sense there, so open on everything instead.
  useEffect(() => {
    setSlotFilter(isBefore(selectedDate, startOfDay(new Date())) ? 'all' : 'upcoming');
  }, [selectedDate]);

  /* ------------------------------------------------------------- selection */

  const selectedSlots = useMemo(
    () => slots.filter((s) => selectedIds.includes(s._id)),
    [slots, selectedIds],
  );

  const toggleSlot = useCallback((slot: Slot) => {
    setSelectedIds((prev) =>
      prev.includes(slot._id) ? prev.filter((id) => id !== slot._id) : [...prev, slot._id],
    );
  }, []);

  const selectGroup = useCallback((groupSlots: Slot[], selectAll: boolean) => {
    const ids = groupSlots.map((s) => s._id);
    setSelectedIds((prev) =>
      selectAll
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id)),
    );
  }, []);

  const selectedViews = selectedSlots.map((s) => getSlotView(s, selectedDate, slotDuration));
  const allBlocked = selectedViews.length > 0 && selectedViews.every((v) => v.state === 'blocked');
  const allOpen = selectedViews.length > 0 && selectedViews.every((v) => v.state === 'available');
  const selectedValue = selectedViews.reduce((sum, v) => sum + (v.price || 0), 0);

  /* --------------------------------------------------------------- actions */

  const setStatus = (status: 'available' | 'unavailable') => {
    if (!selectedIds.length) return;

    dispatch(
      updateVendorVenueSlots({
        payload: {
          venueId,
          sportId,
          courtId,
          slotIds: selectedIds,
          date: apiDate(selectedDate),
          dataToUpdate: { status },
        },
        callback: (res: any) => {
          if (res?.payload?.slots) {
            toast.success(
              status === 'available'
                ? `${selectedIds.length} slot${selectedIds.length === 1 ? '' : 's'} opened for booking`
                : `${selectedIds.length} slot${selectedIds.length === 1 ? '' : 's'} blocked`,
            );
            setSelectedIds([]);
            fetchSlots();
          } else {
            toast.error(res?.message || 'Could not update those slots');
          }
        },
      }),
    );
  };

  /**
   * Payment-link booking: the customer gets a Razorpay link and the slot stays
   * held until they pay. The platform collects the money and owes it back to
   * the vendor, so this is the right choice only when the vendor wants the
   * customer to pay before the slot is committed.
   */
  const handleCreateBooking = (payload: any, cb: any) => {
    dispatch(
      createManualBooking({
        payload: { ...payload, bookingSource: Values.bookingSource.VENDOR },
        callback: (res: any) => {
          if (res?.payload) {
            toast.success(
              `Payment link sent to ${payload.customerName} — ₹${payload?.finalPrice?.toFixed(0)}`,
            );
            setBookingModalOpen(false);
            setSelectedIds([]);
            fetchSlots();
            if (typeof cb === 'function') cb(res?.payload);
          } else {
            toast.error(res?.error || res?.message || 'Could not create that booking');
          }
        },
      }),
    );
  };

  /**
   * Direct booking: the customer pays the venue's own UPI/QR, so no payment
   * link is generated, no money passes through the platform and there is
   * nothing to settle back. The slot is booked and confirmed immediately —
   * the vendor is recording a booking they have already been paid for.
   */
  const handleCreateDirectBooking = (payload: any, cb: any) => {
    dispatch(
      createDirectBooking({
        payload: { ...payload, bookingSource: Values.bookingSource.VENDOR },
        callback: (res: any) => {
          if (res?.payload) {
            const amount = res?.payload?.amountToCollect ?? payload?.finalPrice;
            toast.success(
              `Booking confirmed for ${payload.customerName} — collect ₹${Number(amount).toFixed(0)} at the venue`,
            );
            setBookingModalOpen(false);
            setSelectedIds([]);
            fetchSlots();
            if (typeof cb === 'function') cb(res?.payload);
          } else {
            toast.error(res?.error || res?.message || 'Could not create that booking');
          }
        },
      }),
    );
  };

  const bookingAllowed = toggleBookingByVenueIdResponse?.payload
    ? toggleBookingByVenueIdResponse?.payload?.isBookingAllowed
    : venue?.isBookingAllowed;

  /* ----------------------------------------------------------------- views */

  const venueOptions: PickerOption[] = venues.map((v: any) => ({
    id: v._id,
    label: v.name,
    meta: v?.address?.city || v?.city,
    thumbnail: v?.venueThumbnail,
    live: Boolean(v?.isVerified),
  }));

  const sportOptions: PickerOption[] = sports.map((s: any) => ({ id: s._id, label: s.name }));
  const courtOptions: PickerOption[] = courts.map((c: any) => ({
    id: c._id,
    label: c.courtName,
  }));

  const counts = summarise(slots, selectedDate, slotDuration);
  const tabCounts = filterCounts(slots, selectedDate, slotDuration);

  const loadingVenues = completedVenueDetails.isLoading && !venues.length;
  const loadingBoard = getSlotsByDateResponse.isLoading || VendorVenueDetailsById.isLoading;

  if (loadingVenues) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-flup-ink-3" aria-hidden />
      </div>
    );
  }

  if (!venues.length) {
    return (
      <Surface padding={false} className="max-w-xl mx-auto mt-6">
        <EmptyState
          icon={<Building2 />}
          title="No venue ready to manage yet"
          message="A venue shows up here once its onboarding payment is done and it has been verified. Add a venue to start managing slots."
          actionLabel="Add a venue"
          onAction={() => navigate(routes.venueOnboard)}
        />
      </Surface>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Slot Manager"
        count={courtId ? `${slots.length} slots` : undefined}
        subtitle={moment(selectedDate).format('dddd, D MMMM YYYY')}
        actions={
          <>
            {/* Billing is per venue, so the plan sits with the venue's other
                whole-venue controls rather than inside the slot pickers. */}
            <VenuePlanPill venueId={venueId} venueName={venue?.name} />

            {/* Booking master switch — the one control that affects the whole
                venue, so it stays out of the board and beside the venue name. */}
            <div className="flex items-center gap-2.5 h-10 px-3.5 rounded-flup-sm bg-flup-surface border border-flup-hairline">
              <Switch
                id="booking-toggle"
                disabled={toggleBookingByVenueIdResponse.isLoading || !venue?._id}
                checked={Boolean(bookingAllowed)}
                onCheckedChange={(value) => {
                  dispatch(
                    toggleBookingByVenueId({
                      pathparams: venue?._id,
                      payload: { isBookingAllowed: value },
                      callback: () =>
                        toast.success(value ? 'Bookings turned on' : 'Bookings paused'),
                    }),
                  );
                }}
              />
              <label htmlFor="booking-toggle" className="cursor-pointer">
                <Text
                  variant="labelSemi"
                  tone={bookingAllowed ? 'success' : 'danger'}
                  as="span"
                >
                  {bookingAllowed ? 'Bookings on' : 'Bookings paused'}
                </Text>
              </label>
            </div>

            <Button
              variant="secondary"
              icon={<Ticket className="w-4 h-4" />}
              onClick={() => navigate(routes.venueBookings)}
            >
              Bookings
            </Button>

            <Button
              variant="secondary"
              icon={<Settings className="w-4 h-4" />}
              disabled={!venue?._id}
              onClick={() => {
                dispatch(setCurrentOnboardVenueDetails(venue));
                navigate(`${routes.editVenue}/${venue?._id}`);
              }}
            >
              Venue settings
            </Button>
          </>
        }
      />

      {!bookingAllowed ? (
        <div className="p-3.5 rounded-flup-md bg-flup-warning-soft">
          <Text variant="label" tone="warning" as="p">
            Bookings are paused for this venue — customers can see it but can't book any of the
            slots below. Turn bookings on when you're ready to sell.
          </Text>
        </div>
      ) : null}

        <ContextPicker
          venues={venueOptions}
          venueId={venueId}
          onVenueChange={setVenueId}
          sports={sportOptions}
          sportId={sportId}
          onSportChange={(id) => dispatch(setSelectedSportId(id))}
          courts={courtOptions}
          courtId={courtId}
          onCourtChange={(id) => dispatch(setSelectedCourtId(id))}
          footer={<DayBar value={selectedDate} onChange={setSelectedDate} bare />}
        />

        {loadingBoard ? (
          <BoardSkeleton />
        ) : !courtId ? (
          <Surface padding={false}>
            <EmptyState
              icon={<Building2 />}
              title="This venue has no courts yet"
              message="Add a sport and at least one court in venue settings, and its slots will show up here."
              actionLabel="Open venue settings"
              onAction={() => {
                dispatch(setCurrentOnboardVenueDetails(venue));
                navigate(`${routes.editVenue}/${venue?._id}`);
              }}
            />
          </Surface>
        ) : (
          <>
            <DaySummary
              available={counts.available}
              booked={counts.booked}
              blocked={counts.blocked}
              hold={counts.hold}
              earned={counts.earned}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <FilterTabs
                items={[
                  { key: 'upcoming', label: 'Upcoming', count: tabCounts.upcoming },
                  { key: 'available', label: 'Available', count: tabCounts.available },
                  { key: 'booked', label: 'Booked', count: tabCounts.booked },
                  { key: 'all', label: 'All slots', count: tabCounts.all },
                ]}
                value={slotFilter}
                onChange={(key) => setSlotFilter(key as SlotFilter)}
                className="flex-1"
              />
              <div className="flex items-center gap-3 shrink-0">
                <Text variant="micro" tone="tertiary" as="p" className="hidden lg:block">
                  Tap slots to select them, then choose an action.
                </Text>
                <button
                  type="button"
                  onClick={fetchSlots}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-flup-xs text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                  <Text variant="micro" tone="inherit" as="span">
                    Refresh
                  </Text>
                </button>
              </div>
            </div>

            <SlotBoard
              slots={slots}
              selectedDate={selectedDate}
              slotDurationInMinutes={slotDuration}
              selectedIds={selectedIds}
              filter={slotFilter}
              onToggleSlot={toggleSlot}
              onSelectGroup={selectGroup}
              onShowAll={() => setSlotFilter('all')}
            />
          </>
        )}

        <SelectionBar
          count={selectedIds.length}
          totalValue={selectedValue}
          allBlocked={allBlocked}
          allOpen={allOpen}
          saving={updateVendorVenueSlotsResponse.isLoading}
          onClear={() => setSelectedIds([])}
          onOpen={() => setStatus(Strings.slotStatus.available as 'available')}
          onBlock={() => setStatus(Strings.slotStatus.unavailable as 'unavailable')}
        onBook={() => setBookingModalOpen(true)}
      />

      {bookingModalOpen && selectedSlots.length > 0 ? (
        <CreateBookingModal
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
          selectedSlots={selectedSlots}
          selectedDate={selectedDate}
          handleCreateBooking={handleCreateBooking}
          handleCreateDirectBooking={handleCreateDirectBooking}
          paymentSettings={venue?.paymentSettings}
        />
      ) : null}
    </div>
  );
};

/** Keeps the board's shape while slots load, so the page doesn't jump. */
const BoardSkeleton = () => (
  <div className="space-y-4">
    <div className="h-[52px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
    {Array.from({ length: 2 }).map((_, i) => (
      <Surface key={i} padding="p-5">
        <div className="h-9 w-40 rounded-flup-sm bg-flup-sunken animate-pulse mb-4" />
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((__, j) => (
            <div key={j} className="h-[104px] rounded-flup-md bg-flup-surface-alt animate-pulse" />
          ))}
        </div>
      </Surface>
    ))}
  </div>
);

export default SlotManager;
